import "./settings.styles.css";
import "./profileSettings.styles.scss";
import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { updateOwnProfile } from "../../../service/profile/profile";
import {
  Toast,
  Form,
  Button,
  Input,
  Avatar,
  InputNumber,
} from "@alpaca/framework";
import { Modal } from "antd";

import {
  login as userLogin,
  logout as userLogout,
} from "../../../store/reducer/userInfoReducer";
import { UserInfoSelector } from "../../../store/reduxSelector";
import Settings from "./settings.component";
import SetPassword from "../../../components/ForgotPassword/ForgotPasswordComponents/SetPassword.component";
import Authenticator from "../../../components/authenticator/authenticator.component";
import AuthorizationCodeStatus from "./UserSettingsTabs/AuthorizationCodeStatus.component";
import { useNavigate } from "react-router-dom";
import {
  validPhoneNumberFormat,
  formatPhoneNumber,
} from "../../../helpers/phoneNumberFormat";

export function ProfileSettings(props) {
  const user = UserInfoSelector();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [modalOpen, setModalOpen] = useState(false);
  const [authenticatorModalOpen, setAuthenticatorModalOpen] = useState(false);
  const navigate = useNavigate();
  const toastRef = useRef(null);

  function closeModal() {
    props.onClose(false);
  }

  function logout() {
    navigate("/");
    dispatch(userLogout());
  }

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  async function save() {
    const formValues = await form.validateFields();
    let data = formValues;
    data["profile_name"] = user.username;
    data["company_name"] = user.company;
    try {
      let result = await updateOwnProfile(data);
      if (result.status) {
        toastRef.current.open({
          title: "Record Updated",
          description: "Profile Saved",
          duration: 3,
          status: "success",
        });
        dispatch(
          userLogin({
            ...user,
            email: data.email_address,
            current_environment: 1,
            firstName: data.first_name,
            lastName: data.last_name,
            phone_number: data.phone_number,
            pin: data.pin,
            authorizationExpiry: data.authorization_expiry,
          })
        );
      }
    } catch (error) {
      toastRef.current.open({
        title: "Record Updated fail",
        description: error.message,
        duration: 3,
        status: "error",
      });
    }
  }

  const showToast = (toastData) => {
    toastRef.current.open(toastData);
  };

  return (
    <div className="full-height">
      <Settings />
      <Toast ref={toastRef} />
      <Modal
        footer={null}
        open={modalOpen}
        title={"Reset Password"}
        onCancel={() => setModalOpen(false)}
        children={
          <div className="optional-modal">
            <SetPassword
              userInfo={user}
              onClose={handleCloseModal}
              showToast={showToast}
            />
          </div>
        }
      />
      <Modal
        footer={null}
        open={authenticatorModalOpen}
        title={"Authenticator QR Code"}
        onCancel={() => setAuthenticatorModalOpen(false)}
        children={
          <div className="optional-modal">
            <Authenticator
              userInfo={user}
              onClose={() => setAuthenticatorModalOpen(false)}
            />
          </div>
        }
      />

      <div className="profileSettingsContainer">
        <div className="head-btn-container">
          <div className="head-btn-subheader">
            <h2 className="sectionHeading">Edit Profile</h2>
            <p className="bodyTextSmall">
              Change your information and environment.
            </p>
          </div>
        </div>
        <>
          <Toast ref={toastRef} />

          <header className="profileSettingsHeader">
            <div className="profileSettingsAvatarContainer">
              <Avatar name={`${user.firstName || ""} ${user.lastName || ""}`} />
            </div>
            <div className="profileSettingsHeaderDetails">
              {/* The user's full name */}
              <h3 className="pageTitle textWhite">{`${user.firstName || ""} ${
                user.lastName || ""
              }`}</h3>
              {/* <p className="alttext">Cairns</p> */}
              <div className="profileBadge">
                {/* This would show what type of the user is. EX: Admin, Member, Viewer */}
                <p>{user.role || ""}</p>
              </div>
            </div>
          </header>

          <div className="profile-settings-body">
            <div className="profile-settings-body">
              <div style={{ display: "flex", gap: "10px" }}>
                <Button size="large" onClick={handleOpenModal}>
                  Change Password
                </Button>
                <Button
                  size="large"
                  onClick={() => {
                    setAuthenticatorModalOpen(true);
                  }}
                >
                  Authenticator QR Code
                </Button>
              </div>
              {/* <div className="head-btn-container">
                <h1>Info</h1>
              </div> */}
              <Form form={form} onFinish={save}>
                <div className="profileSettingsFormCol">
                  <Form.Item
                    name="first_name"
                    initialValue={user.firstName}
                    label="First Name"
                    labelCol={{ span: 24 }}
                    style={{ width: "100%" }}
                  >
                    <Input
                      title="First Name"
                      className="user-input"
                      required
                      size="large"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    initialValue={user.lastName}
                    name="last_name"
                    label="Last Name"
                    labelCol={{ span: 24 }}
                    style={{ width: "100%" }}
                  >
                    <Input
                      title="Last Name"
                      value={user.lastName}
                      className="user-input"
                      size="large"
                      required
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>
                <Form.Item
                  initialValue={user.email}
                  name="email_address"
                  label="Email"
                  labelCol={{ span: 24 }}
                >
                  <Input
                    title="Email"
                    className="user-input"
                    required
                    size="large"
                  />
                </Form.Item>
                <div className="profileSettingsFormCol">
                  <Form.Item
                    initialValue={user.phone_number}
                    name="phone_number"
                    label="Phone Number"
                    labelCol={{ span: 24 }}
                    normalize={(value) => formatPhoneNumber(value)}
                    rules={[
                      {
                        pattern: validPhoneNumberFormat,
                        message: "Invalid phone number",
                      },
                    ]}
                  >
                    <Input
                      value={user.phone_number}
                      className="user-input"
                      required
                      size="large"
                    />
                  </Form.Item>

                  <Form.Item
                    initialValue={user.pin}
                    name="pin"
                    label="PIN"
                    labelCol={{ span: 24 }}
                  >
                    <Input
                      title="PIN"
                      value={user.pin}
                      className="user-input"
                      required
                      size="large"
                    />
                  </Form.Item>
                </div>
                <div className="profileSettingsFormCol">
                  <Form.Item
                    initialValue={user.authorizationExpiry}
                    name="authorization_expiry"
                    label="Authorization Code Expiry(mins)"
                    labelCol={{ span: 24 }}
                    tooltip="How long the supervisor authorization code remains valid once first used"
                  >
                    <InputNumber
                      min={1}
                      step={1}
                      value={user.authorizationExpiry}
                      size="large"
                      style={{ flex: 1 }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Supervisor Authorization Code Status"
                    labelCol={{ span: 24 }}
                  >
                    <AuthorizationCodeStatus />
                  </Form.Item>
                </div>
              </Form>
              <footer className="profileSettingsFooter">
                <Button size="large" onClick={logout}>
                  Log Out
                </Button>
                <Button type="primary" size="large" onClick={save}>
                  Save Changes
                </Button>
              </footer>
            </div>
          </div>
        </>
      </div>
    </div>
  );
}

export default ProfileSettings;
